import { useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import { motion } from 'motion/react';
import { useTheme } from '@/contexts/ThemeContext';
import { useUsage } from '@/contexts/UsageContext';
import { useGuestDemo } from '@/contexts/GuestDemoContext';
import { useAuth } from '@/contexts/AuthContext';
import { useAd } from '@/contexts/AdContext';
import Pressable from '@/components/base/Pressable';
import PremiumGate from '@/components/feature/PremiumGate';
import DemoGate from '@/components/feature/DemoGate';

export default function ScanStart() {
  const navigate = useNavigate();
  const location = useLocation();
  const { resolvedTheme } = useTheme();
  const { checkUsage, recordUsage } = useUsage();
  const { canScan, useScan, activeGate, dismissGate } = useGuestDemo();
  const { isAuthenticated } = useAuth();
  const { watchAd, adProvider } = useAd();
  const isDark = resolvedTheme === 'dark';
  const isDesktop = typeof window !== 'undefined' && window.innerWidth >= 1024;

  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const [typedTopic, setTypedTopic] = useState('');
  const [showGate, setShowGate] = useState(false);
  const [gateFeature, setGateFeature] = useState<'image_upload'>('image_upload');

  const consumeScan = useScan;

  const imageInputRef = useRef<HTMLInputElement>(null);
  const docInputRef = useRef<HTMLInputElement>(null);

  const uploadStatus = checkUsage('image_upload');

  const generateTypedLesson = () => {
    const value = typedTopic.trim();
    if (!value) return;
    sessionStorage.removeItem('scan_input_image');
    sessionStorage.setItem('scan_context', JSON.stringify({
      subject: 'General Studies',
      grade: '10',
      topic: value,
      fromTypedPrompt: true,
    }));
    sessionStorage.setItem('scan_instruction', `Teach this question or topic clearly: ${value}`);
    sessionStorage.setItem('scan_output_mode', 'video');
    navigate('/scan/analyzing');
  };

  useEffect(() => {
    if (location.state) {
      sessionStorage.setItem('scan_context', JSON.stringify(location.state));
    }
  }, [location.state]);

  const t = {
    pageBg: isDark ? 'bg-[#0F0F10]' : 'bg-white',
    textPrimary: isDark ? 'text-white' : 'text-neutral-900',
    textSecondary: isDark ? 'text-neutral-400' : 'text-neutral-500',
    textMuted: isDark ? 'text-neutral-500' : 'text-neutral-400',
    textFaint: isDark ? 'text-neutral-700' : 'text-neutral-300',
    cardBg: isDark ? 'bg-[#1C1D21]' : 'bg-white',
    cardBorder: isDark ? 'border-white/[0.05]' : 'border-neutral-100',
    cardShadow: isDark ? 'shadow-none' : 'shadow-[0_2px_8px_rgba(0,0,0,0.04)]',
    iconBg: isDark ? 'bg-white/[0.04]' : 'bg-neutral-100',
    divider: isDark ? 'border-white/[0.04]' : 'border-neutral-100',
    accentBg: isDark ? 'bg-neutral-800' : 'bg-neutral-100',
    lowBadge: isDark ? 'bg-amber-500/10 text-amber-400 border-amber-500/20' : 'bg-amber-50 text-amber-600 border-amber-200',
    emptyBadge: isDark ? 'bg-red-500/10 text-red-400 border-red-500/20' : 'bg-red-50 text-red-600 border-red-200',
    adBadge: isDark ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' : 'bg-emerald-50 text-emerald-600 border-emerald-200',
  };

  const handleCameraClick = () => {
    if (!canScan) {
      setShowGate(true);
      return;
    }
    if (!uploadStatus.allowed) {
      setGateFeature('image_upload');
      setShowGate(true);
      return;
    }
    navigate('/scan/camera');
  };

  const handleGalleryClick = () => {
    if (!canScan) {
      setShowGate(true);
      return;
    }
    if (!uploadStatus.allowed) {
      setGateFeature('image_upload');
      setShowGate(true);
      return;
    }
    imageInputRef.current?.click();
  };

  const proceedGalleryFile = (file: File) => {
    if (!consumeScan()) return;
    recordUsage('image_upload');
    const reader = new FileReader();
    reader.onload = (event) => {
      const result = event.target?.result as string;
      setCapturedImage(result);
      sessionStorage.setItem('scan_input_image', result);
      navigate('/scan/output');
    };
    reader.readAsDataURL(file);
  };

  const handleGalleryFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    proceedGalleryFile(file);
    e.target.value = '';
  };

  const handleDocumentClick = () => {
    if (!canScan) {
      setShowGate(true);
      return;
    }
    if (!uploadStatus.allowed) {
      setGateFeature('image_upload');
      setShowGate(true);
      return;
    }
    docInputRef.current?.click();
  };

  const proceedDocumentFile = (file: File) => {
    if (!consumeScan()) return;
    recordUsage('image_upload');
    const reader = new FileReader();
    reader.onload = (event) => {
      const result = event.target?.result as string;
      setCapturedImage(result);
      sessionStorage.setItem('scan_input_image', result);
      sessionStorage.setItem('scan_input_type', 'document');
      navigate('/scan/output');
    };
    reader.readAsDataURL(file);
  };

  const handleDocumentFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    proceedDocumentFile(file);
    e.target.value = '';
  };

  const options = [
    {
      id: 'camera',
      icon: 'ri-camera-3-line',
      label: 'Take a Picture',
      description: 'Use your camera to scan homework instantly',
      onClick: handleCameraClick,
      accent: isDark ? 'bg-white/[0.08]' : 'bg-neutral-100',
      iconColor: isDark ? 'text-white' : 'text-neutral-900',
      desktop: false,
    },
    {
      id: 'gallery',
      icon: 'ri-image-line',
      label: 'Upload Image',
      description: 'Select a photo from your gallery',
      onClick: handleGalleryClick,
      accent: isDark ? 'bg-white/[0.06]' : 'bg-neutral-100',
      iconColor: isDark ? 'text-[#A1A1A6]' : 'text-neutral-500',
      desktop: true,
    },
    {
      id: 'pdf',
      icon: 'ri-file-pdf-line',
      label: 'Upload PDF',
      description: 'Select a PDF document from your files',
      onClick: handleDocumentClick,
      accent: isDark ? 'bg-white/[0.06]' : 'bg-neutral-100',
      iconColor: isDark ? 'text-[#A1A1A6]' : 'text-neutral-500',
      desktop: true,
    },
  ].filter((opt) => opt.desktop !== false || !isDesktop);

  const getUploadBadge = () => {
    if (uploadStatus.remaining === 0) return 'limit-reached';
    if (uploadStatus.remaining <= 2) return 'low';
    return null;
  };

  const uploadBadge = getUploadBadge();

  // Ad-unlock handlers for extra scans
  const unlockGalleryWithAd = () => {
    watchAd(
      'extra_scan',
      'Extra Scan',
      'unlock 1 additional scan',
      () => imageInputRef.current?.click() // after ad, open file picker directly
    );
  };

  const unlockCameraWithAd = () => {
    watchAd(
      'extra_scan',
      'Extra Scan',
      'unlock 1 additional scan',
      () => navigate('/scan/camera')
    );
  };

  const unlockDocumentWithAd = () => {
    watchAd(
      'extra_scan',
      'Extra Scan',
      'unlock 1 additional scan',
      () => docInputRef.current?.click()
    );
  };

  return (
    <div className={`flex flex-col min-h-full ${t.pageBg} transition-colors duration-500 lg:max-w-5xl lg:mx-auto lg:w-full`}>
      {/* Header */}
      <div className="px-5 pt-8 pb-5 flex items-center gap-3 lg:px-8 lg:pt-6">
        <Pressable
          onClick={() => navigate('/')}
          className={`w-9 h-9 flex items-center justify-center rounded-xl ${t.iconBg} transition-colors duration-500`}
          scale={0.88}
        >
          <div className="w-5 h-5 flex items-center justify-center">
            <i className={`ri-arrow-left-line ${t.textMuted}`} />
          </div>
        </Pressable>
        <div>
          <p className={`text-[11px] font-medium ${t.textMuted} uppercase tracking-wider`}>Scan Homework</p>
          <h1 className={`text-lg font-bold leading-tight ${t.textPrimary}`}>Choose Input</h1>
        </div>
      </div>

      {/* Usage badge */}
      {uploadBadge && (
        <div className="px-5 pb-3">
          <div className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-[11px] font-medium ${uploadBadge === 'low' ? t.lowBadge : t.emptyBadge}`}>
            <div className="w-3 h-3 flex items-center justify-center">
              <i className={`${uploadBadge === 'low' ? 'ri-alert-line' : 'ri-close-circle-line'} text-[10px]`} />
            </div>
            {uploadStatus.remaining === 0
              ? 'Upload limit reached today'
              : `${uploadStatus.remaining} upload${uploadStatus.remaining === 1 ? '' : 's'} left today`}
          </div>
        </div>
      )}

      {/* Ad unlock hint when limit reached */}
      {adProvider !== null && uploadStatus.remaining === 0 && (
        <div className="px-5 pb-3">
          <div className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-[11px] font-medium ${t.adBadge}`}>
            <div className="w-3 h-3 flex items-center justify-center">
              <i className="ri-movie-ai-line text-[10px]" />
            </div>
            <span>Out of scans?</span>
            <button onClick={unlockGalleryWithAd} className="font-semibold underline cursor-pointer">
              Watch an ad
            </button>
            <span>for +1 scan, or</span>
            <button onClick={() => setShowGate(true)} className="font-semibold underline cursor-pointer">
              go Premium
            </button>
          </div>
        </div>
      )}

      {/* Demo hint for guests */}
      {!canScan && !isAuthenticated && (
        <div className="px-5 pb-3">
          <div className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-[11px] font-medium ${isDark ? 'bg-blue-500/10 text-blue-400 border-blue-500/20' : 'bg-blue-50 text-blue-600 border-blue-200'}`}>
            <div className="w-3 h-3 flex items-center justify-center">
              <i className="ri-information-line text-[10px]" />
            </div>
            <span>Demo scan used.</span>
            <button onClick={() => navigate('/auth/signup')} className="font-semibold underline cursor-pointer">
              Create an account
            </button>
            <span>for unlimited scans.</span>
          </div>
        </div>
      )}

      {/* Unlimited indicator for authenticated users */}
      {isAuthenticated && (
        <div className="px-5 pb-3">
          <div className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-[11px] font-medium ${isDark ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' : 'bg-emerald-50 text-emerald-600 border-emerald-200'}`}>
            <div className="w-3 h-3 flex items-center justify-center">
              <i className="ri-check-double-line text-[10px]" />
            </div>
            <span>Unlimited scans active</span>
          </div>
        </div>
      )}

      {/* Hero */}
      <div className="px-5 pb-6">
        <div className="relative w-full rounded-2xl p-6 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-neutral-900 via-neutral-950 to-black" />
          <div className="absolute -top-10 left-1/2 -translate-x-1/2 w-48 h-48 bg-white/5 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-0 w-32 h-32 bg-white/3 rounded-full blur-2xl" />
          <div className="relative z-10">
            <div className="w-12 h-12 rounded-xl bg-white/10 backdrop-blur-sm flex items-center justify-center mb-3">
              <div className="w-6 h-6 flex items-center justify-center">
                <i className="ri-scan-line text-white text-xl" />
              </div>
            </div>
            <h2 className="text-base font-bold text-white mb-1">AI Homework Scanner</h2>
            <p className="text-xs text-white/50 leading-relaxed max-w-[240px]">
              Snap a photo or upload any school work. Our AI reads it and gives you step-by-step solutions or video explanations.
            </p>
          </div>
        </div>
      </div>

      {/* Options */}
      <div className="px-5 pb-5">
        <div className={`rounded-2xl border ${t.cardBorder} ${t.cardBg} ${t.cardShadow} p-4`}>
          <div className="mb-3 flex items-center gap-2">
            <i className={`ri-keyboard-line ${t.textMuted}`} />
            <div>
              <p className={`text-sm font-semibold ${t.textPrimary}`}>Type a question or topic</p>
              <p className={`text-[11px] ${t.textMuted}`}>Create the same visual lesson without uploading a photo.</p>
            </div>
          </div>
          <div className={`flex gap-2 rounded-xl border ${t.divider} p-2 ${isDark ? 'bg-white/[0.03]' : 'bg-neutral-50'}`}>
            <input
              value={typedTopic}
              onChange={(event) => setTypedTopic(event.target.value)}
              onKeyDown={(event) => { if (event.key === 'Enter') generateTypedLesson(); }}
              placeholder="e.g. Explain vectors in 2D"
              className={`min-w-0 flex-1 bg-transparent px-2 text-xs outline-none ${t.textPrimary}`}
            />
            <button
              type="button"
              onClick={generateTypedLesson}
              disabled={!typedTopic.trim()}
              className="rounded-lg bg-neutral-900 px-3 py-2 text-[11px] font-semibold text-white disabled:opacity-40"
            >
              Generate Lesson
            </button>
          </div>
        </div>
      </div>

      {/* Options */}
      <div className="px-5 pb-8 space-y-3">
        {options.map((opt) => (
          <Pressable
            key={opt.id}
            onClick={opt.onClick}
            className={`flex items-center gap-4 ${t.cardBg} border ${t.cardBorder} ${t.cardShadow} rounded-2xl p-4 w-full transition-colors duration-500`}
            scale={0.97}
          >
            <div className={`w-12 h-12 rounded-xl ${opt.accent} flex items-center justify-center flex-shrink-0`}>
              <div className="w-6 h-6 flex items-center justify-center">
                <i className={`${opt.icon} ${opt.iconColor} text-xl`} />
              </div>
            </div>
            <div className="flex-1 min-w-0 text-left">
              <h3 className={`text-sm font-semibold ${t.textPrimary}`}>{opt.label}</h3>
              <p className={`text-[11px] ${t.textMuted} leading-relaxed`}>{opt.description}</p>
            </div>
            <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
              <i className={`ri-arrow-right-s-line ${t.textFaint} text-lg`} />
            </div>
          </Pressable>
        ))}

        {/* Ad unlock action row when limit reached */}
        {adProvider !== null && uploadStatus.remaining === 0 && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.35 }}
            className={`flex flex-col gap-2 mt-2`}
          >
            <Pressable
              onClick={unlockCameraWithAd}
              className={`w-full flex items-center gap-3 px-4 py-3.5 rounded-xl border ${t.adBadge} transition-colors duration-200`}
              scale={0.97}
            >
              <div className="w-8 h-8 rounded-lg bg-neutral-900/10 flex items-center justify-center flex-shrink-0">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-camera-3-line text-xs" />
                </div>
              </div>
              <div className="flex-1 text-left">
                <p className="text-sm font-semibold">Watch Ad & Scan with Camera</p>
                <p className="text-[11px] opacity-80">Unlock 1 extra scan instantly</p>
              </div>
              <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                <i className="ri-arrow-right-s-line text-sm" />
              </div>
            </Pressable>
            <Pressable
              onClick={unlockGalleryWithAd}
              className={`w-full flex items-center gap-3 px-4 py-3.5 rounded-xl border ${t.adBadge} transition-colors duration-200`}
              scale={0.97}
            >
              <div className="w-8 h-8 rounded-lg bg-neutral-900/10 flex items-center justify-center flex-shrink-0">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-image-line text-xs" />
                </div>
              </div>
              <div className="flex-1 text-left">
                <p className="text-sm font-semibold">Watch Ad & Upload Image</p>
                <p className="text-[11px] opacity-80">Unlock 1 extra upload instantly</p>
              </div>
              <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                <i className="ri-arrow-right-s-line text-sm" />
              </div>
            </Pressable>
          </motion.div>
        )}
      </div>

      {/* Recent Scans teaser */}
      <div className="px-5 pb-8">
        <div className={`flex items-center gap-2 mb-3`}>
          <i className={`ri-history-line ${t.textMuted} text-xs`} />
          <h3 className={`text-xs font-bold ${t.textMuted} uppercase tracking-wider`}>Recent Scans</h3>
        </div>
        <Pressable
          onClick={() => navigate('/history')}
          className={`w-full flex items-center gap-3 px-4 py-3.5 rounded-xl border ${t.cardBorder} ${isDark ? 'bg-white/[0.02] hover:bg-white/[0.04]' : 'bg-neutral-50 hover:bg-neutral-100'} transition-colors duration-200`}
          scale={0.97}
        >
          <div className="w-8 h-8 rounded-lg bg-neutral-900 flex items-center justify-center flex-shrink-0">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-folder-open-line text-white text-xs" />
            </div>
          </div>
          <div className="flex-1 text-left">
            <p className={`text-sm font-semibold ${t.textPrimary}`}>View Scan History</p>
            <p className={`text-[11px] ${t.textMuted}`}>All your scanned homework and AI results</p>
          </div>
          <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
            <i className={`ri-arrow-right-s-line ${t.textFaint} text-sm`} />
          </div>
        </Pressable>
      </div>

      <input
        ref={imageInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleGalleryFile}
      />
      <input
        ref={docInputRef}
        type="file"
        accept="image/*,.pdf,.doc,.docx,.txt"
        className="hidden"
        onChange={handleDocumentFile}
      />

      <PremiumGate
        feature={gateFeature}
        open={showGate}
        onClose={() => setShowGate(false)}
      />

      <DemoGate
        feature="scan"
        open={!!activeGate}
        onClose={dismissGate}
      />
    </div>
  );
}